// Scheduled tasks of an AI agent (`proto/agent_tasks.proto`, Rust `commands/agent_tasks.rs`).
// Field names mirror the Rust DTOs (snake_case); proto enums are passed as their numbers.

export enum AgentTaskKind {
  ONCE = 0,
  CRON = 1,
  INTERVAL = 2,
}

export enum AgentTaskStatus {
  ACTIVE = 0,
  PAUSED = 1,
  /** A one-off task ran (or was finally skipped). */
  COMPLETED = 2,
  CANCELLED = 3,
}

export enum AgentTaskRunStatus {
  NONE = 0,
  RUNNING = 1,
  SUCCEEDED = 2,
  FAILED = 3,
  /** Not run: the owner has no subscription/credits, or the author may no longer call the agent. */
  SKIPPED = 4,
  /** The run time passed without the service handling it. */
  MISSED = 5,
}

export enum AgentTaskAction {
  PAUSE = 0,
  RESUME = 1,
  CANCEL = 2,
}

export interface AgentTaskSettings {
  max_tasks_per_agent: number
  /** Minimum period between runs of a repeating task. */
  min_interval_seconds: number
  max_title_length: number
  max_prompt_length: number
  default_time_zone: string
  history_retention_days: number
}

export interface AgentTask {
  id: string
  bot_id: string
  /** Who created the task — it runs on their behalf. */
  author_id: string
  channel_id: string
  group_id: string
  title: string
  /** The instruction the agent runs on every run. */
  prompt: string
  kind: AgentTaskKind
  /** 5-field cron (kind = CRON). */
  cron_expression?: string | null
  /** Period in seconds (kind = INTERVAL). */
  interval_seconds: number
  /** ONCE — the run time; INTERVAL — the first run (periods count from it). */
  run_at?: string | null
  /** IANA zone the cron is read in. */
  time_zone: string
  status: AgentTaskStatus
  /** Next run; null — no more runs (paused, completed, cancelled). */
  next_run_at?: string | null
  last_run_at?: string | null
  last_status: AgentTaskRunStatus
  last_error?: string | null
  runs_count: number
  /** Created by the agent itself from chat, not by the owner on this page. */
  created_by_agent: boolean
  created_at?: string | null
  updated_at?: string | null
}

export interface AgentTasks {
  tasks: AgentTask[]
}

/** The schedule part shared by create and update requests. */
export interface AgentTaskSchedule {
  kind: AgentTaskKind
  cron_expression?: string | null
  interval_seconds?: number
  /** ISO string. */
  run_at?: string | null
  time_zone?: string | null
}

export interface CreateAgentTaskRequest extends AgentTaskSchedule {
  bot_id: string
  channel_id: string
  title: string
  prompt: string
}

/** Partial: omitted fields stay as they are; a set `kind` replaces the whole schedule. */
export interface UpdateAgentTaskRequest extends Partial<AgentTaskSchedule> {
  bot_id: string
  task_id: string
  title?: string | null
  prompt?: string | null
  channel_id?: string | null
}

export interface AgentTaskRun {
  /** Scheduled run time (the record's key). */
  scheduled_at?: string | null
  run_id: string
  status: AgentTaskRunStatus
  started_at?: string | null
  finished_at?: string | null
  /** Why the run didn't succeed. */
  error?: string | null
  input_tokens: number
  output_tokens: number
  /** Micro-credits charged; 0 on the owner's own key. */
  charged_micro: number
}

export interface AgentTaskRuns {
  runs: AgentTaskRun[]
  /** Cursor (`before`) of the next, older page; null — last page. */
  next_before?: string | null
  micro_per_credit: number
}
